import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { Navigate, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertTriangle, CheckCircle2, Clock, Calendar, MapPin, Package, FileText, Mail } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog'
import { useAuth } from '@/hooks/useAuth'
import { getProviderInboxFn, setProviderInterestFn } from '@/lib/firebase/functions'

type InboxData = Awaited<ReturnType<typeof getProviderInboxFn>>['data']
type InboxRequest = InboxData['requests'][number]
type InboxAssignment = InboxData['assignments'][number]

type State =
  | { kind: 'loading' }
  | { kind: 'unauthorized'; message: string }
  | { kind: 'ready'; data: InboxData }
  | { kind: 'error'; message: string }

function fmtDate(ms: number | null | undefined): string {
  if (!ms) return ''
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function fmtScheduledDate(ymd: string | null): string {
  if (!ymd) return 'No date set'
  const [Y, M, D] = ymd.split('-').map(Number)
  if (!Y || !M || !D) return ymd
  return new Date(Y, M - 1, D).toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' })
}

export function ProviderPortalPage() {
  const { user, loading: authLoading } = useAuth()
  const [state, setState] = useState<State>({ kind: 'loading' })
  const [pendingId, setPendingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setState({ kind: 'loading' })
    try {
      const { data } = await getProviderInboxFn({})
      setState({ kind: 'ready', data })
    } catch (err: unknown) {
      const message = formatUserFacingError(err, 'Failed to load.')
      const code = (err as { code?: string }).code
      if (code === 'functions/permission-denied' || code === 'functions/unauthenticated') {
        setState({ kind: 'unauthorized', message })
      } else {
        setState({ kind: 'error', message })
      }
    }
  }, [])

  useEffect(() => {
    if (authLoading) return
    if (!user) return
    void load()
  }, [user, authLoading, load])

  async function handleInterest(requestId: string, status: 'available' | 'declined' | 'withdrawn') {
    setPendingId(requestId)
    try {
      await setProviderInterestFn({ requestId, status })
      await load()
    } catch (err) {
      const message = formatUserFacingError(err, 'Failed to update.')
      alert(message)
    } finally {
      setPendingId(null)
    }
  }

  if (authLoading) return null
  if (!user) {
    return <Navigate to="/sign-in" replace state={{ returnTo: '/portal/requests' }} />
  }

  if (state.kind === 'loading') {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12 space-y-6">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-5 w-96" />
        <div className="space-y-3 pt-4">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-32 w-full" />)}
        </div>
      </div>
    )
  }

  if (state.kind === 'unauthorized') {
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center space-y-5">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/10 text-amber-600">
          <AlertTriangle className="h-8 w-8" />
        </div>
        <h1 className="text-2xl font-semibold">Provider portal access</h1>
        <p className="text-destructive text-sm">{state.message}</p>
        <p className="text-muted-foreground text-sm">
          This portal is for ODD team members marked as <em>Available for Hire</em>. If you should have access,
          contact{' '}
          <a href="mailto:joseph@onedaydance.com" className="text-foreground underline">joseph@onedaydance.com</a>.
        </p>
      </div>
    )
  }

  if (state.kind === 'error') {
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center space-y-4">
        <p className="text-destructive">{state.message}</p>
        <Button onClick={() => void load()}>Retry</Button>
      </div>
    )
  }

  const { member, requests, assignments } = state.data
  const openRequests = requests
    .filter((r) => r.status !== 'converted' && r.status !== 'declined')
    .sort((a, b) => (b.createdAtMs ?? 0) - (a.createdAtMs ?? 0))
  const upcomingAssignments = [...assignments]
    .filter((assignment) => assignment.status !== 'delivered' && assignment.status !== 'cancelled')
    .sort((a, b) => (a.scheduledDate ?? '9999-12-31').localeCompare(b.scheduledDate ?? '9999-12-31'))
  const assignmentHistory = [...assignments]
    .filter((assignment) => assignment.status === 'delivered' || assignment.status === 'cancelled')
    .sort((a, b) => (b.scheduledDate ?? '').localeCompare(a.scheduledDate ?? ''))

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 space-y-10">
      <header className="space-y-1">
        <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Provider Portal</p>
        <h1 className="text-3xl font-bold">Hi {member.name.split(' ')[0]}</h1>
        <p className="text-muted-foreground">
          Service requests that match your roles, plus the bookings you're assigned to.
        </p>
        <div className="flex gap-2 pt-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/portal/invoices">Invoices</Link>
          </Button>
        </div>
      </header>

      <section className="space-y-4">
        <div className="flex items-baseline justify-between">
          <h2 className="text-xl font-semibold">Open Requests</h2>
          <span className="text-xs text-muted-foreground">{openRequests.length} matching</span>
        </div>

        {openRequests.length === 0 ? (
          <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            Nothing matching your roles right now. We'll email you when something comes in.
          </p>
        ) : (
          <div className="space-y-3">
            {openRequests.map((req) => (
              <RequestCard
                key={req.id}
                req={req}
                pending={pendingId === req.id}
                onMarkAvailable={() => void handleInterest(req.id, 'available')}
                onDecline={() => void handleInterest(req.id, 'declined')}
                onWithdraw={() => void handleInterest(req.id, 'withdrawn')}
              />
            ))}
          </div>
        )}
      </section>

      <Separator />

      <section className="space-y-4">
        <div className="flex items-baseline justify-between">
          <h2 className="text-xl font-semibold">Upcoming &amp; Active Bookings</h2>
          <span className="text-xs text-muted-foreground">{upcomingAssignments.length} active</span>
        </div>

        {upcomingAssignments.length === 0 ? (
          <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            No upcoming or active bookings right now.
          </p>
        ) : (
          <div className="space-y-2">
            {upcomingAssignments.map((assignment) => (
              <AssignmentRow key={assignment.id} assignment={assignment} />
            ))}
          </div>
        )}
      </section>

      {assignmentHistory.length > 0 && (
        <>
          <Separator />
          <section className="space-y-4">
            <div className="flex items-baseline justify-between">
              <h2 className="text-xl font-semibold">Booking History</h2>
              <span className="text-xs text-muted-foreground">{assignmentHistory.length} total</span>
            </div>
            <div className="space-y-2">
              {assignmentHistory.map((assignment) => (
                <AssignmentRow key={assignment.id} assignment={assignment} />
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  )
}

function AssignmentRow({ assignment: a }: { assignment: InboxAssignment }) {
  const hasLineDetails = a.lineItems.some((li) => li.date || li.time || li.location)
  const hasDetails =
    hasLineDetails ||
    !!a.description ||
    !!a.estimateNote ||
    !!a.clientAdditionalInfo ||
    !!a.clientEmail ||
    a.equipmentNames.length > 0

  return (
    <Dialog>
      <div className="rounded-md border p-4 flex flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-center">
        <div className="min-w-0">
          <p className="font-medium truncate">{a.title}</p>
          <p className="text-xs text-muted-foreground flex items-center gap-3 mt-1 flex-wrap">
            <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {fmtScheduledDate(a.scheduledDate)}</span>
            <span>· Client: {a.clientName}</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {hasDetails && (
            <DialogTrigger asChild>
              <Button size="sm" variant="outline">View details</Button>
            </DialogTrigger>
          )}
          <Badge variant={a.status === 'cancelled' ? 'destructive' : 'default'}>{a.status}</Badge>
        </div>
      </div>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{a.title}</DialogTitle>
          <DialogDescription>
            {fmtScheduledDate(a.scheduledDate)} · Client: {a.clientName}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 text-sm">
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Booking contact
            </h3>
            <div className="flex flex-wrap gap-2">
              <Button asChild size="sm" variant="outline">
                <a href={`mailto:${a.clientEmail}`}>
                  <Mail className="mr-1.5 h-3.5 w-3.5" /> Email {a.clientName}
                </a>
              </Button>
            </div>
          </section>

          {a.lineItems.length > 0 && (
            <section className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Services
              </h3>
              <ul className="space-y-2">
                {a.lineItems.map((li, i) => (
                  <li key={i} className="rounded border p-2.5 space-y-1">
                    <p className="font-medium">{li.description}</p>
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
                      {li.date && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3 w-3" /> {li.date}
                        </span>
                      )}
                      {li.time && (
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {li.time}
                        </span>
                      )}
                      {li.location && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> {li.location}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {a.equipmentNames.length > 0 && (
            <section className="space-y-1.5">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <Package className="h-3.5 w-3.5" /> Equipment to bring
              </h3>
              <ul className="list-disc pl-5 space-y-0.5">
                {a.equipmentNames.map((n, i) => (
                  <li key={i}>{n}</li>
                ))}
              </ul>
            </section>
          )}

          {(a.clientAdditionalInfo || a.description || a.estimateNote) && (
            <section className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5" /> Notes
              </h3>
              {a.clientAdditionalInfo && (
                <div>
                  <p className="text-xs text-muted-foreground">From the client</p>
                  <p className="whitespace-pre-wrap">{a.clientAdditionalInfo}</p>
                </div>
              )}
              {a.description && (
                <div>
                  <p className="text-xs text-muted-foreground">Job description</p>
                  <p className="whitespace-pre-wrap">{a.description}</p>
                </div>
              )}
              {a.estimateNote && (
                <div>
                  <p className="text-xs text-muted-foreground">Admin note</p>
                  <p className="whitespace-pre-wrap">{a.estimateNote}</p>
                </div>
              )}
            </section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface RequestCardProps {
  req: InboxRequest
  pending: boolean
  onMarkAvailable: () => void
  onDecline: () => void
  onWithdraw: () => void
}

function RequestCard({ req, pending, onMarkAvailable, onDecline, onWithdraw }: RequestCardProps) {
  const isAvailable = req.myInterestStatus === 'available'
  const isDeclined = req.myInterestStatus === 'declined'

  return (
    <div className={`rounded-lg border p-5 space-y-3 ${isAvailable ? 'border-emerald-500/40 bg-emerald-500/5' : ''}`}>
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row">
        <div>
          <p className="font-medium">
            {req.submitterName}
            {req.organizationName && <span className="text-muted-foreground"> · {req.organizationName}</span>}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
            <Clock className="h-3 w-3" />
            Submitted {fmtDate(req.createdAtMs)}
          </p>
        </div>
        {isAvailable && (
          <Badge variant="default" className="flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> You're available
          </Badge>
        )}
        {isDeclined && (
          <Badge variant="secondary">You declined</Badge>
        )}
      </div>

      <ul className="space-y-1.5">
        {req.services.map((s, i) => (
          <li key={i} className="text-sm flex items-start gap-2">
            <span className="font-medium">{s.subType}</span>
            <span className="text-muted-foreground text-xs flex items-center gap-3 mt-0.5 flex-wrap">
              {s.date && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{s.date}</span>}
              {s.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{s.location}</span>}
              {s.hours !== null && <span>{s.hours}h</span>}
            </span>
          </li>
        ))}
      </ul>

      {req.additionalInfo && (
        <p className="text-xs text-muted-foreground italic">"{req.additionalInfo}"</p>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        {isAvailable ? (
          <Button size="sm" variant="outline" disabled={pending} onClick={onWithdraw}>
            {pending ? 'Updating…' : 'Withdraw availability'}
          </Button>
        ) : isDeclined ? (
          <Button size="sm" variant="outline" disabled={pending} onClick={onMarkAvailable}>
            {pending ? 'Updating…' : 'Change to Available'}
          </Button>
        ) : (
          <>
            <Button size="sm" disabled={pending} onClick={onMarkAvailable}>
              {pending ? 'Updating…' : 'Mark Available'}
            </Button>
            <Button size="sm" variant="ghost" disabled={pending} onClick={onDecline}>
              Not interested
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
